import { apiClient } from './api';

export interface OAuthStatus {
  provider: string;
  connected_at: string;
  last_contacts_sync: string | null;
  last_calendar_sync: string | null;
  last_email_sync: string | null;
  sync_schedule: string | null;
  scopes: string[];
}

export interface ImportJob {
  id: number;
  provider: string;
  job_type: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  records_processed: number;
  records_failed: number;
  tier_limit: number | null;
  started_at: string | null;
  completed_at: string | null;
  error_log: string[] | null;
  created_at: string;
}

export interface ScheduleStatus {
  provider: string;
  schedule: 'hourly' | 'daily' | 'weekly' | null;
  next_run: string | null;
  /** crm-per-project-scoping: project synced contacts go to (null = Unassigned). */
  crm_target_project_id?: string | null;
}

export interface ConsentStatus {
  provider: string;
  email_consent_at: string | null;
}

export interface ClioConnectionStatus {
  connected: boolean;
  connected_at: string | null;
  expires_at: string | null;
}

export const integrationsApi = {
  getStatus: () => apiClient.get<OAuthStatus[]>('/integrations/status'),
  getMicrosoftAuthUrl: (scopeLevel: string = 'contacts') =>
    apiClient.get<{ auth_url: string }>(`/integrations/microsoft/auth?scope_level=${scopeLevel}`),
  getGoogleAuthUrl: (scopeLevel: string = 'contacts') =>
    apiClient.get<{ auth_url: string }>(`/integrations/google/auth?scope_level=${scopeLevel}`),
  // calendar_write (read+write for booking sync) is requested via getMicrosoft/GoogleAuthUrl('calendar_write')
  disconnect: (provider: string) => apiClient.delete(`/integrations/${provider}`),
  triggerSync: (
    provider: string,
    types: string[],
    start_date?: string,
    end_date?: string,
    folder_filter?: string,
  ) =>
    apiClient.post<{ job_ids: Record<string, number> }>('/integrations/sync', {
      provider,
      types,
      ...(start_date ? { start_date } : {}),
      ...(end_date ? { end_date } : {}),
      ...(folder_filter ? { folder_filter } : {}),
    }),
  getJobs: () => apiClient.get<ImportJob[]>('/integrations/jobs'),
  getJob: (jobId: number) => apiClient.get<ImportJob>(`/integrations/jobs/${jobId}`),
  getSchedule: () => apiClient.get<ScheduleStatus[]>('/integrations/schedule'),
  setSchedule: (provider: string, schedule: string | null) =>
    apiClient.post('/integrations/schedule', { provider, schedule }),
  setCrmTarget: (provider: string, crmTargetProjectId: string | null) =>
    apiClient.put<{ success: boolean; crm_target_project_id: string | null }>('/integrations/crm-target', {
      provider,
      crm_target_project_id: crmTargetProjectId,
    }),
  getConsent: () => apiClient.get<ConsentStatus[]>('/integrations/consent'),
  giveEmailConsent: (provider: string) =>
    apiClient.post('/integrations/consent/email', { provider }),
  getPreviewCount: (provider: string, startDate: string, endDate: string, folderFilter: string) =>
    apiClient.get<{ estimated_count: number; tier_limit: number | null }>(
      `/integrations/preview-count?provider=${provider}&start_date=${startDate}&end_date=${endDate}&folder_filter=${folderFilter}`,
    ),
  // Clio
  getClioAuthUrl: () => apiClient.get<{ auth_url: string }>('/clio/auth'),
  getClioStatus: () => apiClient.get<ClioConnectionStatus>('/clio/status'),
  triggerClioSync: () => apiClient.post<{ job_id: number }>('/clio/sync'),
};
